import { Directive, EventEmitter, OnInit, Output, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { AlertService } from 'src/app/alerts/alerts.service';
import { DynamicEvent } from 'src/app/configuration/datadog/models/datadog.model';
import { DatadogLogsService } from 'src/app/configuration/datadog/services/datadog-logs.service';
import { OriginsEnum } from 'src/app/configuration/enums/origins.enum';
import { PaymentModulesEnum } from 'src/app/configuration/payment-modules/payment-module-class';
import { OrderState } from 'src/app/features/checkout/states/order.state';
import { PaymentTypeNamesEnum } from 'src/app/models/payments.model';
import { BreakpointService } from 'src/app/services/breakspoint.service';
import { CheckoutGtmFacade } from 'src/app/services/checkout-gtm.facade';
import { InstallmentsService } from 'src/app/services/installments.service';
import { MyCartService } from 'src/app/services/my-cart.service';
import { TransactionErrorsService } from 'src/app/services/transaction-errors.service';
import {
  selectCompany,
  selectShowInstallmentCost,
} from 'src/app/store/states/company/company.selectors';
import { selectConfigOrigin } from 'src/app/store/states/config/config.selectors';
import {
  selectPayment,
  selectPaymentMaxInstallment,
  selectPaymentModule,
  selectPaymentType,
} from 'src/app/store/states/payment/payment.selectors';
import { PaymentState } from 'src/app/store/states/payment/payment.types';
import {
  selectHasCreditFee,
  selectPurchasePaymentCondition,
} from 'src/app/store/states/purchase/purchase.selectors';
import { InstallmentWithRecommended } from 'src/app/store/states/purchase/purchase.types';

@Directive()
export class InstallmentsDropdownAbstract implements OnInit {
  protected alertService = inject(AlertService);
  @Output() handleOnSelect = new EventEmitter<InstallmentWithRecommended>();
  readonly enableRecommendationWhenStarting = input(true);
  protected readonly checkoutGtm = inject(CheckoutGtmFacade);
  protected transactionErrorService = inject(TransactionErrorsService);
  private datadogLogsService = inject(DatadogLogsService);
  protected isHandSet = toSignal(inject(BreakpointService).isHandset$);
  protected store = inject(Store);
  protected myCartService$ = inject(MyCartService).myCartSelectedInvoices$;
  protected recommendationService = inject(InstallmentsService);
  protected isCollapsed = true;
  selectedInstallment = this.recommendationService.selectedInstallment;
  private orderState = inject(OrderState);
  protected installments = this.orderState.installments;
  protected filloutInstallments = false;
  protected hasCreditFee = this.store.selectSignal(selectHasCreditFee);
  protected paymentType = this.store.selectSignal(selectPaymentType);
  protected paymentModule = this.store.selectSignal(selectPaymentModule);
  protected origin = this.store.selectSignal(selectConfigOrigin);
  protected maxInstallments = this.store.selectSignal(selectPaymentMaxInstallment);
  private companyState = this.store.selectSignal(selectCompany);
  private payment = this.store.select(selectPayment);
  protected originsEnum = OriginsEnum;
  protected paymentTypenamesEnum = PaymentTypeNamesEnum;
  protected paymentModulesEnum = PaymentModulesEnum;
  protected showInstallmentCost = this.store.selectSignal(selectShowInstallmentCost);

  protected isFixedPaymentConditions = computed(
    () => this.paymentModule() == PaymentModulesEnum.FIXED_PAYMENT_CONDITIONS
  );
  protected isResetRecommended = computed(
    () =>
      this.origin() == OriginsEnum.SABESP_AGV ||
      this.paymentModule() == PaymentModulesEnum.PAYMENT_METHOD_SELECT
  );

  ngOnInit(): void {
    this.payment.subscribe((payment) => {
      this.handleInstallments(payment);
    });
  }

  handleInstallments(payment: PaymentState) {
    if (this.paymentModule() === PaymentModulesEnum.FIXED_PAYMENT_CONDITIONS) {
      const installment: InstallmentWithRecommended = {
        installment: payment.order.paymentConditionsInfo?.installmentsNumber as number,
        value: payment.order.paymentConditionsInfo?.installmentsAmount as number,
        recommendationWhenChoosingAnInstallment: false,
        recommendationWhenStarting: false,
      };
      if (installment.installment) this.setSelectedInstallment(installment);
    }
  }

  handleFilloutInstallments(value: boolean) {
    this.filloutInstallments = value;
  }

  public setSelectedInstallment(installment: InstallmentWithRecommended) {
    this.recommendationService.setSelectedInstallment(installment.installment);
    this.logParcelChangeEvent(installment);
    this.handleOnSelect.emit(this.selectedInstallment() as InstallmentWithRecommended);
  }

  private logParcelChangeEvent(installment: InstallmentWithRecommended) {
    const paymentCondition = this.store.selectSignal(selectPurchasePaymentCondition);
    const logData: DynamicEvent = {
      action: 'click',
      event_type: 'Parcela Selecionada',
      data_event: {
        installment: installment.installment,
        value: installment.value,
        fee: paymentCondition()?.credit.fees[installment.installment],
        recommended_installment: installment.recommendationWhenChoosingAnInstallment,
        recommended_super: installment.recommendationWhenStarting,
      },
    };

    // this.datadogLogsService.handleEventsAndEmitLogs<DataEventChangeInstallment>(logData);
  }

  protected onSelect(installment: InstallmentWithRecommended) {
    this.checkoutGtm.pushSelectContent({
      item_id: 'flexpag-web_web_botao_meio_11',
      content_type: `botao-${installment.installment}-parcelas`,
      item_text: 'modal-selecao-de-parcelas',
    });
    this.setSelectedInstallment(installment);
    this.filloutInstallments = false;

    if (this.isHandSet()) {
      this.closeBottomSheet();
      return;
    }
    this.toggleDrawer();
  }

  protected closeBottomSheet(): void {
    this.alertService.bottomSheetRef.dismiss();
  }

  protected toggleDrawer() {
    this.recommendationService.toggleDrawer();
  }

  protected handleCloseDrawer() {
    this.checkoutGtm.pushSelectContent({
      item_id: 'flexpag-web_web_botao_meio_11',
      item_text: 'modal-selecao-de-parcelas',
      content_type: 'botao-fechar',
    });
    this.recommendationService.toggleDrawer();
  }
}
